import dayjs from "dayjs";

export const generateCsvMedicalTests = (suppliers) => {
  const flattenObject = (obj, prefix = "") => {
    const flat = {};
    for (let key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newKey = prefix ? `${prefix}_${key}` : key;

        if (Array.isArray(obj[key])) {
          obj[key].forEach((item, index) => {
            if (typeof item === "object" && item !== null) {
              Object.assign(flat, flattenObject(item, `${newKey}_${index}`));
            } else {
              flat[`${newKey}_${index}`] = item;
            }
          });
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          Object.assign(flat, flattenObject(obj[key], newKey));
        } else {
          flat[newKey] = obj[key];
        }
      }
    }
    return flat;
  };

  const flattenedSuppliers = suppliers.map(flattenObject);

  const csvHeaders = Object.keys(flattenedSuppliers[0]).map(
    (key) => key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
  );
  const csvRows = flattenedSuppliers.map((supplier) => Object.values(supplier));

  const csvContent = [csvHeaders, ...csvRows]
    .map((e) => e.join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "exportCSV.csv";
  link.click();
};

export const generateCSV = (data) => {
  const header = [
    "Order ID",
    "Supplier Name",
    "Supplier Phone",
    "Supplier GST No",
    "Supplier Address",
    "Supplier Email",
    "Bill Type",
    "Bill Number",
    "Purchase Date",
    "Grand Total",
    "Item ID",
    "HSN Code",
    "Batch",
    "Expiry Date",
    "Quantity",
    "CGST",
    "SGST",
    "Rate",
    "MRP",
    "Amount",
    "Discount",
  ];

  const rows = data.flatMap((order) => {
    return order.purchase_items.map((item) => {
      return [
        order.id,
        order.supplier.name,
        order.supplier.phone,
        order.supplier.gst_no,
        order.supplier.address,
        order.supplier.email,
        order.bill_type,
        order.bill_number,
        order.purchase_date,
        order.grand_total,
        item.id,
        item.hsn_code,
        item.batch,
        item.expiry_date,
        item.quantity,
        item.cgst,
        item.sgst,
        item.rate,
        item.mrp,
        item.amount,
        item.discount,
      ];
    });
  });

  const csvContent = [header, ...rows].map((e) => e.join(",")).join("\n");

  // Create a Blob from the CSV content
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  // Create a link and trigger a download
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "purchase_orders.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleShowJson = (suppliers) => {
  const jsonWindow = window.open("", "_blank");
  jsonWindow.document.write(
    "<pre>" + JSON.stringify(suppliers, null, 2) + "</pre>",
  );
  jsonWindow.document.title = "Suppliers JSON Data";
};

export const handleDownloadCsvMedicine = (suppliers) => {
  const csvHeaders = [
    Object.keys(suppliers[0]).map(
      (key) =>
        key?.charAt(0)?.toUpperCase() + key?.slice(1)?.replace(/_/g, " "),
    ),
  ];
  const csvRows = suppliers.map((supplier) => Object.values(supplier));

  const csvContent = [csvHeaders, ...csvRows]
    .map((e) => e.join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "exportCSV.csv";
  link.click();
};

export const handleDownloadCsvSupplier = (suppliers) => {
  const csvHeaders = [
    Object.keys(suppliers[0]).map(
      (key) =>
        key?.charAt(0)?.toUpperCase() + key?.slice(1)?.replace(/_/g, " "),
    ),
  ];
  const csvRows = suppliers.map((supplier) => Object.values(supplier));

  const csvContent = [csvHeaders, ...csvRows]
    .map((e) => e.join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "exportCSV.csv";
  link.click();
};

// JS equivalent of Ruby's round_to_nearest_five
// round_off=0 → no rounding; null/undefined → default unit 5
// export const roundToNearestUnit = (number, roundOff = null) => {
//   const parsed = parseFloat(roundOff);
//   if (roundOff !== null && roundOff !== undefined && parsed === 0) return number;

//   const unit = !isNaN(parsed) && parsed > 0 ? parsed : 5.0;

//   if (number % (unit * 2) === 0) return number;

//   const mod = number % (unit * 2);
//   if (mod === unit || mod > unit) {
//     return Math.ceil(number / unit) * unit;
//   } else {
//     return Math.floor(number / unit) * unit;
//   }
// };

/**
 * Round a money amount to the nearest configured unit.
 * Mirrors backend ApplicationHelper#round_to_nearest_five for the ₹5 step, and does
 * true nearest rounding for every other unit (₹1, ₹0.5, …).
 *
 *   roundOff > 0           -> round to nearest `roundOff`
 *   roundOff === 0         -> no rounding (explicit "off" from admin)
 *   roundOff null/blank/NaN -> default unit 5
 *
 * - unit === 5 keeps the legacy window rule (0–5 → down, 5–10 → up) that WDC runs on,
 *   so existing ₹5 bills are untouched (e.g. 57 → 60).
 * - every other unit uses true nearest rounding; the window rule mis-rounds those
 *   (e.g. ₹1 would turn 32.9 → 32 and 33.1 → 34).
 * - negatives (returns/refunds) are rounded by magnitude and re-signed, because JS `%`
 *   gives a negative remainder for negative dividends.
 */
export const roundToNearestUnit = (number, roundOff = null) => {
  const num = parseFloat(number);
  if (isNaN(num)) return 0;

  const parsed = parseFloat(roundOff);
  if (roundOff !== null && roundOff !== undefined && parsed === 0) return Math.round(num);

  const unit = !isNaN(parsed) && parsed > 0 ? parsed : 5.0;

  const sign = num < 0 ? -1 : 1;
  const n = Math.abs(num);

  let result;
  if (unit === 5) {
    // legacy ₹5 window — keep WDC's existing behavior
    const period = unit * 2;
    const mod = n % period;
    if (mod === 0) result = n;
    else if (mod === unit || mod > unit) result = Math.ceil(n / unit) * unit;
    else result = Math.floor(n / unit) * unit;
  } else {
    // true nearest rounding for ₹1, ₹0.5, and any other unit
    result = Math.round(n / unit) * unit;
  }

  return sign * result;
};
export const formattedDate = (date) => {
  if (!date) return null;

  const parsedDate = dayjs(date, "YYYY-MM-DD");
  const formattedDate = parsedDate.format("MMM DD, YYYY");
  const isExpiringSoon = parsedDate.isBefore(dayjs().add(2, "month"));
  const style = isExpiringSoon
    ? {
        // backgroundColor: "red",
        color: "red",
        padding: "5px 10px",
        borderRadius: "4px",
      }
    : {};

  return <span style={style}>{formattedDate}</span>;
};

export const formattedDateWithoutBg = (date) => {
  if (!date) return null;

  const parsedDate = dayjs(date, "YYYY-MM-DD");
  const formattedDate = parsedDate.format("MMMM DD, YYYY");
  return <span>{formattedDate}</span>;
};

export const isExpiredDate = (date) => {
  if (!date) return false;

  const parsedDate = dayjs(date, "YYYY-MM-DD");
  return parsedDate.isBefore(dayjs(), "day");
};

export const expiryMedicineFormate = (date) => {
  if (!date) return null;

  const parsedDate = dayjs(date, "YYYY-MM-DD");
  const formattedDate = parsedDate.format("MMM DD, YYYY");
  const isExpired = parsedDate.isBefore(dayjs(), "day"); // strictly before today
  const isExpiringSoon =
    !isExpired && parsedDate.isBefore(dayjs().add(2, "month"));

  let style = {};
  if (isExpired) {
    style = {
      // color: "white",
      color: "red",
      padding: "5px 10px",
      borderRadius: "4px",
      fontWeight: "400",
    };
  }
  //  else if (isExpiringSoon) {
  //   style = {
  //     color: "red",
  //     padding: "5px 10px",
  //     borderRadius: "4px",
  //   };
  // }

  return <span style={style}>{formattedDate}</span>;
};

export const highLightUPcomingMonthsExpire = (date) => {
  if (!date) return null;

  const parsedDate = dayjs(date); // Your date is already in YYYY-MM-DD format
  const formattedDate = parsedDate.format("MMM DD, YYYY");

  const today = dayjs();
  const threeMonthsFromNow = today.add(3, "month");

  // Highlight if already expired or expiring within next 3 months
  const isExpiredOrExpiringSoon = parsedDate.isBefore(threeMonthsFromNow);

  const style = isExpiredOrExpiringSoon
    ? {
        color: "red",
        padding: "5px 10px",
        borderRadius: "4px",
      }
    : {};

  return <span style={style}>{formattedDate}</span>;
};

export const getCurrentDate = () => {
  return new Date().toISOString().split("T")[0];
};

export const isTodayDate = (dateString) => {
  if (!dateString) return false;

  const invoiceDate = dayjs(dateString.split(" ")[0], "DD-MM-YYYY");

  const today = dayjs().tz("Asia/Kolkata").startOf("day");

  return invoiceDate.isSame(today, "day");
};
